import {
    Contract,
    ProductConfigOption,
    ServiceConfigOption,
} from '@/modules/Contract/types'
import { useEffect } from 'react'
import { Path, useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import Component from '../../../Config'

interface ConfigurationProps {
    product_config_options: ProductConfigOption[]
    index: number
    isPOC?: boolean
}

const Configuration: React.FC<ConfigurationProps> = ({
    product_config_options,
    index,
    isPOC = false,
}) => {
    const { t } = useTranslation()
    const { control } = useFormContext<Contract>()

    const key = `order.products.[${index}].service_configoptions`

    const { fields, remove, replace } = useFieldArray({
        control,
        name: key as any,
    })

    const service_configoptions = useWatch({
        control,
        name: key as Path<Contract>,
    }) as ServiceConfigOption[]

    const product_form = useWatch({
        control,
        name: `order.products.[${index}].product_form` as Path<Contract>,
    })

    const servicePriceModel = useWatch({
        control,
        name: `order.products.[${index}].service_price_model` as Path<Contract>,
    })

    useEffect(() => {
        const options = (product_config_options || [])?.map((option) => {
            const oldOption = service_configoptions?.find(
                (s) => s.name === option.name
            )
            const commitmentValue =
                oldOption?.commitment_value ?? option.value ?? 0
            const commitmentPricePerUnit =
                oldOption?.commitment_price_per_unit ?? option?.price ?? 0

            return {
                name: option.name,
                unit: option.unit,
                type: option.type,
                price_config: {
                    method: 'progressive',
                },
                commitment_prepaid: true,
                statementable: false,
                commitment_price_per_unit: commitmentPricePerUnit || undefined,
                commitment_price_total:
                    oldOption?.commitment_price_total ??
                    commitmentValue * commitmentPricePerUnit,
                ...(oldOption || {}),
                commitment_value: commitmentValue,
                commitment_time: product_form === 'commit' ? 1 : 0,
                resizable: option?.is_editable,
                product_form: 'not_commit',
                prices: option.product_pricing_steps
                    ?.sort((a, b) => a.price_start - b.price_start)
                    .map((step) => ({
                        price_start: step.price_start,
                        price_end: step.price_end,
                        price_per_unit: step.price_per_unit,
                    })),
            }
        })

        replace(options)
    }, [product_form, product_config_options, replace])

    return (
        <div className="w-full">
            <Typogaphy size="medium" className="font-medium">
                {t('configuration-information')}
            </Typogaphy>

            <div className="mt-4 w-full">
                {fields?.map((option, i) => {
                    // @ts-ignore
                    const Comp = Component[option.type]
                    if (!Comp) return null

                    return (
                        <Comp
                            key={option.id}
                            index={i}
                            name={key}
                            productIndex={index}
                            isPOC={isPOC}
                            remove={remove}
                            servicePriceModel={servicePriceModel}
                        />
                    )
                })}
            </div>
        </div>
    )
}

export default Configuration
